import React, { useMemo, useCallback } from "react";
import DataTableBase from "../interfaccia/DataTableBase";
import associaImmagineENomeANomeUtente from "../interfaccia/funzioniComuni/associaImmagineENomeANomeUtente";
import stilePulsanti from "../interfaccia/stilePulsanti";

function ModaArticoliTable({
  data,
  loading,
  totalRows,
  page,
  perPage,
  perPageOptions,
  sortBy,
  sortDirection,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  currentUserEmail = "",
  title = "Tabella MODA_articoli",
  noDataComponent,
  onOpenProduct,
}) {
  const { tastoApriAspetto } = stilePulsanti;
  const normalizeBoolean = useCallback((value) => {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!normalized.length) {
        return false;
      }
      if (["1", "true", "si", "sì", "yes", "y"].includes(normalized)) {
        return true;
      }
      if (["0", "false", "no", "n"].includes(normalized)) {
        return false;
      }
    }
    return Boolean(value);
  }, []);
  const renderUserAvatar = useCallback(
    (row, keys) => {
      const rawValue =
        keys
          .map((key) => row?.[key])
          .find(
            (value) => typeof value === "string" && value.trim().length > 0,
          ) ?? "";
      if (!rawValue) {
        return <span className="gray f7">-</span>;
      }
      const avatar = associaImmagineENomeANomeUtente(
        rawValue,
        currentUserEmail,
        64,
      );
      const nomeDisplay =
        avatar?.nomeDisplay?.trim() || rawValue?.trim() || "Utente";
      const src = avatar?.pathImmagine;
      return (
        <div className="tc" title={nomeDisplay}>
          <div
            className="center br-100 bg-light-gray dib"
            style={{ width: 36, height: 36 }}
          >
            {src ? (
              <img
                src={src}
                alt={nomeDisplay}
                className="br-100"
                style={{ width: 36, height: 36 }}
              />
            ) : (
              <span className="f6 lh-copy flex items-center justify-center h-100">
                {nomeDisplay.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        </div>
      );
    },
    [currentUserEmail],
  );

  const columns = useMemo(
    () => [
      {
        name: "Cod.VS",
        selector: (row) =>
          row.codiceVS ?? row.codice_vs ?? row.cod_vs ?? row.codicevs ?? "",
        cell: (row) => {
          const codice =
            row.codiceVS ?? row.codice_vs ?? row.cod_vs ?? row.codicevs ?? "";
          const isOnlyPs = Boolean(row.pubbl_solo_su_ps);
          return (
            <button
              type="button"
              className={`pointer bn bg-transparent flex items-center ${
                Number(row.annullato) === 1 ? "dark-red" : ""
              }`}
              onClick={() => {
                if (isOnlyPs) {
                  const conferma = window.confirm(
                    "Attenzione! questo è un articolo vecchio ed è presente solo su prestahop. Se continui verrà creato un documento vuoto in Domino e dovrai inserire tutti i dati. Ti consiglio di recuperare testi e tutto da prestashop.",
                  );
                  if (!conferma) {
                    return;
                  }
                }
                onOpenProduct?.({
                  codVS: codice,
                  row,
                });
              }}
            >
              <strong className="mr2">{codice}</strong>
              <span>{tastoApriAspetto}</span>
            </button>
          );
        },
        sortable: false,
        /* sortField: "codiceVS", */
        minWidth: "100px",
        compact: true,
      },
      {
        name: "Import",
        cell: (row) => {
          const username = String(row.ut_import_done ?? row.utImportDone ?? "").trim();
          if (!username) return <span className="gray f7">-</span>;
          const date = row.timeImportDoneItaly || row.time_import_done || "Data non registrata";
          return <span title={`Ultimo import: ${username} · ${date}`} aria-label={`Ultimo import: ${username} · ${date}`}>
            {renderUserAvatar(row, ["ut_import_done", "utImportDone"])}
          </span>;
        },
        sortable: true,
        sortField: "utImportDone",
        width: "65px",
        center: true,
        compact: true,
      },
      {
        name: "P.Testi",
        cell: (row) => {
          const rawValue =
            ["ut_pubbl_done", "utPubblDone", "pubblicatiDa"]
              .map((key) => row?.[key])
              .find(
                (value) => typeof value === "string" && value.trim().length > 0,
              ) ?? "";
          const isPublished =
            row.pubblicato &&
            (row.pubblicato ?? "").toString().toUpperCase() !== "NO";

          // Se l'utente che ha pubblicato è presente, mostra l'avatar
          if (rawValue) {
            return renderUserAvatar(row, [
              "ut_pubbl_done",
              "utPubblDone",
              "pubblicatiDa",
            ]);
          }

          // Se non c'è l'utente ma l'articolo è pubblicato (pubblicato !== "NO"), mostra un cerchio con punto interrogativo
          if (isPublished) {
            return (
              <div className="tc">
                <div
                  className="center br-100 sfondoColScuro dib flex items-center justify-center"
                  style={{ width: 36, height: 36 }}
                >
                  <span className="f5 b">?</span>
                </div>
              </div>
            );
          }

          // Altrimenti mostra il trattino
          return <span className="gray f7">-</span>;
        },
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        name: "P.Imm",
        cell: (row) => {
          const rawValue =
            ["ut_imm_done", "utImmDone", "immaginiDa"]
              .map((key) => row?.[key])
              .find(
                (value) => typeof value === "string" && value.trim().length > 0,
              ) ?? "";
          const hasImages = row.ha_immagine ?? false;

          // Se l'utente che ha caricato le immagini è presente, mostra l'avatar
          if (rawValue) {
            return renderUserAvatar(row, [
              "ut_imm_done",
              "utImmDone",
              "immaginiDa",
            ]);
          }

          // Se non c'è l'utente ma le immagini esistono, mostra un cerchio con punto interrogativo
          if (hasImages) {
            return (
              <div className="tc">
                <div
                  className="center br-100 sfondoColScuro dib flex items-center justify-center"
                  style={{ width: 36, height: 36 }}
                >
                  <span className="f5 b">?</span>
                </div>
              </div>
            );
          }

          // Altrimenti mostra il trattino
          return <span className="gray f7">-</span>;
        },
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        name: "A.Testi",
        cell: (row) =>
          renderUserAvatar(row, [
            "ut_pubbl_pren",
            "utPubblPren",
            "assegnatoTesti",
          ]),
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },
      {
        name: "A.Imm",
        cell: (row) =>
          renderUserAvatar(row, [
            "ut_imm_pren",
            "utImmPren",
            "assegnatoImmagini",
          ]),
        sortable: false,
        width: "60px",
        center: true,
        compact: true,
      },

      {
        name: "Nome",
        selector: (row) =>
          row.nome ??
          row.descrizione ??
          row.descrizioneBreve ??
          row.descrizione_breve ??
          "",
        sortable: true,
        sortField: "nome",
        minWidth: "250px",
        grow: 2,
        wrap: true,
      },
      {
        name: "Colore",
        selector: (row) =>
          row.colore ?? row.nomeColore ?? row.nome_colore ?? "",
        sortable: true,
        sortField: "colore",
        minWidth: "140px",
        wrap: true,
      },
      {
        name: "Brand",
        selector: (row) => row.brand ?? row.brand_nome ?? row.brandName ?? "",
        sortable: true,
        sortField: "brand",
        minWidth: "80px",
        compact: true,
      },
      {
        name: "StockW",
        selector: (row) => row.stock ?? "",
        sortable: true,
        sortField: "stock",
        width: "100px",
        compact: true,
      },
      {
        name: "StockM",
        selector: (row) => row.stockModa ?? row.stock_moda ?? "",
        sortable: true,
        sortField: "stockModa",
        width: "110px",
        compact: true,
      },
      {
        name: "Pers.",
        selector: (row) =>
          row.persona ?? row.target_persona ?? row.target ?? "",
        sortable: true,
        sortField: "persona",
        width: "50px",
        style: { minWidth: "50px", maxWidth: "50px" },
        headerStyle: {
          minWidth: "50px",
          maxWidth: "50px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "Stag.",
        selector: (row) =>
          row.stagione ?? row.season ?? row.stagione_corrente ?? "",
        sortable: true,
        sortField: "stagione",
        width: "50px",
        style: { minWidth: "50px", maxWidth: "50px" },
        headerStyle: {
          minWidth: "50px",
          maxWidth: "50px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "Pubblicato",
        selector: (row) => row.pubblicato ?? "",
        sortable: true,
        sortField: "pubblicato",
        minWidth: "110px",
        wrap: true,
      },
      {
        name: "Codice Produttore",
        selector: (row) =>
          row.codiceProduttore ??
          row.codice_produttore ??
          row.codiceProd ??
          row.cod_prod ??
          "",
        sortable: true,
        sortField: "codProd",
        minWidth: "160px",
        wrap: true,
      },
      {
        name: "Codice Modello",
        selector: (row) =>
          row.codiceModello ??
          row.codice_modello ??
          row.codModello ??
          row.codMod ??
          row.cod_mod ??
          "",
        sortable: true,
        sortField: "codiceModello",
        minWidth: "160px",
        wrap: true,
      },
      {
        name: "Codice Colore",
        selector: (row) =>
          row.codiceColore ??
          row.codice_colore ??
          row.codColore ??
          row.codCol ??
          row.cod_col ??
          "",
        sortable: true,
        sortField: "codiceColore",
        minWidth: "100px",
        wrap: true,
        compact: true,
      },
      {
        name: "Rep.",
        selector: (row) =>
          row.reparto ??
          row.repartoDescrizione ??
          row.reparto_descrizione ??
          "",
        sortable: true,
        sortField: "reparto",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "Cat.",
        selector: (row) =>
          row.categoria ??
          row.categoria_path ??
          row.categoriaPath ??
          row.percorso_categoria ??
          "",
        sortable: true,
        sortField: "categoria",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "Tipo",
        selector: (row) =>
          row.tipoOggetto ??
          row.tipo_oggetto ??
          row.tipo ??
          row.tipologia ??
          "",
        sortable: true,
        sortField: "tipoOggetto",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "Sottotipo",
        selector: (row) =>
          row.sottotipo ?? row.sotto_tipo ?? row.subtipo ?? row.subtype ?? "",
        sortable: true,
        sortField: "sottotipo",
        width: "70px",
        style: { minWidth: "70px", maxWidth: "70px" },
        headerStyle: {
          minWidth: "70px",
          maxWidth: "70px",
          whiteSpace: "normal",
        },
        wrap: true,
        compact: true,
      },
      {
        name: "idPS",
        selector: (row) => row.id_ps ?? row.idPs ?? "",
        sortable: true,
        sortField: "id_ps",
        width: "60px",
        compact: true,
      },
      {
        name: "Testi importati",
        selector: (row) =>
          row.testi_importati ?? row.testiImportati ?? row.testiimportati ?? 0,
        cell: (row) => {
          const value =
            row.testi_importati ??
            row.testiImportati ??
            row.testiimportati ??
            0;
          const isImported =
            typeof value === "string"
              ? value.trim() !== "" && value !== "0"
              : Number(value) === 1;
          return (
            <span className={isImported ? "dark-green" : "gray"}>
              {isImported ? "Si" : "No"}
            </span>
          );
        },
        sortable: false,
        minWidth: "120px",
        center: true,
        compact: true,
      },
      {
        name: "Cat. principale",
        selector: (row) => row.cat_principale ?? row.catPrincipale ?? "",
        sortable: true,
        sortField: "cat_principale",
        minWidth: "160px",
        wrap: true,
      },
      {
        name: "Attivo PS",
        selector: (row) =>
          normalizeBoolean(
            row.attivo ?? row.attivo_ps ?? row.attivoPS ?? row.ps_attivo,
          ),
        cell: (row) => {
          const attivo = normalizeBoolean(
            row.attivo ?? row.attivo_ps ?? row.attivoPS ?? row.ps_attivo,
          );
          return (
            <span className={attivo ? "dark-green" : "dark-red"}>
              {attivo ? "✔" : "✘"}
            </span>
          );
        },
        sortable: false,
        minWidth: "80px",
        center: true,
        compact: true,
      },
      {
        name: "Vis.",
        selector: (row) => {
          const value = (row.visibility ?? "").toString().toLowerCase();
          if (value === "both") {
            return "Si";
          }
          if (value === "none") {
            return "No";
          }
          return row.visibility ?? "";
        },
        sortable: true,
        sortField: "visibility",
        minWidth: "40px",
        wrap: true,
        compact: true,
      },
      {
        name: "Motivo",
        selector: (row) =>
          row.deactivation_reason ?? row.deactivationReason ?? "",
        sortable: false,
        minWidth: "160px",
        wrap: true,
      },
      {
        name: "Imm.",
        selector: (row) => {
          const hasImages =
            row.hasImmagini ?? row.has_immagini ?? row.ha_immagine ?? false;
          return hasImages ? "Si" : "No";
        },
        cell: (row) => {
          const hasImages =
            row.hasImmagini ?? row.has_immagini ?? row.ha_immagine ?? false;
          return (
            <span className={hasImages ? "dark-green" : "gray"}>
              {hasImages ? "Si" : "No"}
            </span>
          );
        },
        sortable: false,
        minWidth: "40px",
        center: true,
        compact: true,
      },
      {
        name: "I. non si trova",
        selector: (row) => {
          const value =
            row.imm_nonsitrova ??
            row.immNonSiTrova ??
            row.imageNotFound ??
            false;
          return value ? "Si" : "No";
        },
        sortable: true,
        sortField: "imm_nonsitrova",
        minWidth: "70px",
        wrap: true,
        compact: true,
      },
      {
        name: "Ultima mod.",
        selector: (row) => {
          const rawDate =
            row.sourceUpdatedAt ?? row.source_updated_at ?? row.sourceupdateat;
          if (!rawDate) {
            return "";
          }
          const date = new Date(rawDate);
          if (Number.isNaN(date.getTime())) {
            return rawDate;
          }
          return date.toLocaleString("it-IT");
        },
        sortable: true,
        sortField: "source_updated_at",
        minWidth: "170px",
      },
      {
        name: "48h Vendite",
        selector: (row) =>
          row["48OreUltimaVendita"] ??
          row["48oreultimavendita"] ??
          row["48OreUltimaVendita".toLowerCase()] ??
          "",
        sortable: false,
        width: "120px",
        right: true,
      },
    ],
    [renderUserAvatar, normalizeBoolean],
  );

  return (
    <div className="sfondoBiancoTema br3">
      <div className="pa3 bb bordoColAlt flex justify-between items-center">
        <div>
          <h4 className="ma0">{title}</h4>
          <p className="ma0 f7 gray">
            Risultati{" "}
            {totalRows > 0
              ? `${(page - 1) * perPage + 1}–${Math.min(
                  page * perPage,
                  totalRows,
                )} di ${totalRows}`
              : "0"}
          </p>
        </div>
      </div>
      <DataTableBase
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={perPageOptions}
        paginationDefaultPage={page}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        sortServer
        onSort={(column, direction) =>
          onSort(column.sortField ?? column.name, direction)
        }
        defaultSortField={sortBy}
        defaultSortAsc={sortDirection !== "desc"}
        highlightOnHover
        dense
        noDataComponent={
          noDataComponent ?? "Nessun articolo trovato per i filtri correnti."
        }
        keyField="id"
      />
    </div>
  );
}

export default ModaArticoliTable;
